import React, { useState, useEffect } from 'react'
import Close from '@material-ui/icons/Close'
import MixedContentPreview from '../UiElements/MixedContentPreview'
import './QuestionDetailModal.css'

// Component for parent answer summary with swipe functionality
const ParentAnswerSummary = ({ summary, questionId, studentId }) => {
  const [showVideos, setShowVideos] = useState(false)
  const [touchStart, setTouchStart] = useState(null)
  const [touchCurrent, setTouchCurrent] = useState(null)
  const [swipeOffset, setSwipeOffset] = useState(0)
  const [videoRecommendations, setVideoRecommendations] = useState([])

  // Load video recommendations from Firestore
  useEffect(() => {
    if (questionId && studentId) {
      loadVideoRecommendations()
    }
  }, [questionId, studentId])

  const loadVideoRecommendations = async () => {
    try {
      const { db } = await import('../../helpers/firebase')
      const { collection, getDocs } = await import('firebase/firestore')

      const videosRef = collection(db, 'users', studentId, 'studyQuestions', questionId, 'videoRecommendations')
      const videosSnapshot = await getDocs(videosRef)
      const videos = videosSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }))
      setVideoRecommendations(videos)
    } catch (error) {
      console.error('Error loading video recommendations:', error)
    }
  }

  const handleTouchStart = (e) => {
    setTouchStart(e.targetTouches[0].clientX)
    setTouchCurrent(e.targetTouches[0].clientX)
    setSwipeOffset(0)
  }

  const handleTouchMove = (e) => {
    if (!touchStart) return

    const currentX = e.targetTouches[0].clientX
    setTouchCurrent(currentX)

    const offset = currentX - touchStart

    // Only allow swipe left if there are videos to show and not already showing
    // Only allow swipe right if videos are showing
    if (videoRecommendations.length > 0) {
      if (!showVideos && offset < 0) {
        // Swiping left, allow up to container width
        setSwipeOffset(Math.max(offset, -window.innerWidth * 0.9))
      } else if (showVideos && offset > 0) {
        // Swiping right, allow up to container width
        setSwipeOffset(Math.min(offset, window.innerWidth * 0.9))
      }
    }
  }

  const handleTouchEnd = () => {
    if (!touchStart || !touchCurrent) {
      setSwipeOffset(0)
      return
    }

    const distance = touchStart - touchCurrent
    const minSwipeDistance = 50

    if (videoRecommendations.length > 0) {
      if (distance > minSwipeDistance && !showVideos) {
        // Swiped left
        setShowVideos(true)
      } else if (distance < -minSwipeDistance && showVideos) {
        // Swiped right
        setShowVideos(false)
      }
    }

    // Reset touch positions and offset
    setTouchStart(null)
    setTouchCurrent(null)
    setSwipeOffset(0)
  }

  const handleVideoClick = (url) => {
    window.open(url, '_blank', 'noopener,noreferrer')
  }

  return (
    <div
      className="parent-answer-summary"
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      style={{ overflow: 'hidden', position: 'relative', width: '100%' }} // Ensure it can contain the sliding panels
    >
      <div className={`summary-container ${showVideos ? 'videos-visible' : ''}`} style={{
        display: 'flex',
        transition: touchStart ? 'none' : 'transform 0.3s ease-in-out',
        transform: touchStart
          ? `translateX(calc(${showVideos ? '-50%' : '0%'} + ${swipeOffset}px))`
          : showVideos ? 'translateX(-50%)' : 'translateX(0)'
      }}>
        <div className="summary-content" style={{ flex: '0 0 50%', width: '50%' }}>
          <h3>Summary</h3>
          <div className="summary-text">
            {summary}
          </div>
          {!showVideos && videoRecommendations.length > 0 && (
            <div className="swipe-hint swipe-hint-summary">
              Swipe for videos »
            </div>
          )}
        </div>

        {videoRecommendations.length > 0 && (
          <div className="videos-panel" style={{ flex: '0 0 50%', width: '50%' }}>
            <h3>Recommended Videos</h3>
            <div className="video-thumbnails">
              {videoRecommendations.map((video) => (
                <div
                  key={video.id}
                  className="video-thumbnail-card"
                  onClick={() => handleVideoClick(video.url)}
                >
                  <div className="video-thumbnail-placeholder">
                    {video.thumbnail ? (
                      <img src={video.thumbnail} alt={video.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    ) : (
                      <div className="video-play-icon">▶</div>
                    )}
                  </div>
                  <div className="video-thumbnail-title">{video.title}</div>
                </div>
              ))}
            </div>
            {showVideos && (
              <div className="swipe-hint swipe-hint-videos">
                « Swipe for summary
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}


const QuestionDetailModal = ({ question, onClose, selectedStudent }) => {
  const [currentAttemptIndex, setCurrentAttemptIndex] = useState(0)
  const [attemptTouchStart, setAttemptTouchStart] = useState(null)
  const [attemptTouchCurrent, setAttemptTouchCurrent] = useState(null)
  const [attemptSwipeOffset, setAttemptSwipeOffset] = useState(0)
  const [loadedVideoRecommendations, setLoadedVideoRecommendations] = useState([])

  useEffect(() => {
    const loadVideoRecommendations = async () => {
      if (!question?.id || !selectedStudent?.id) return
      try {
        const { db } = await import('../../helpers/firebase')
        const { collection, getDocs } = await import('firebase/firestore')
        const videosRef = collection(db, 'users', selectedStudent.id, 'studyQuestions', question.id, 'videoRecommendations')
        const videosSnapshot = await getDocs(videosRef)
        const videos = videosSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))
        setLoadedVideoRecommendations(videos)
      } catch (error) {
        console.error('Error loading video recommendations:', error)
      }
    }
    loadVideoRecommendations()
  }, [question?.id, selectedStudent?.id])

  // Get all attempts
  const attempts = question.attempts && question.attempts.length > 0
    ? question.attempts
    : question.allAttempts && question.allAttempts.length > 0
    ? question.allAttempts
    : [{
        answer: question.answer,
        feedback: question.feedback,
        studentAnswer: question.studentAnswer
      }]

  const questionText = question.question?.latex ||
                      question.question?.text ||
                      question.questionText ||
                      (typeof question.question === 'string' ? question.question : 'No question text')

  // Minimum swipe distance (in px)
  const minSwipeDistance = 100

  const onAttemptTouchStart = (e) => {
    setAttemptTouchStart(e.targetTouches[0].clientX)
    setAttemptTouchCurrent(e.targetTouches[0].clientX)
    setAttemptSwipeOffset(0)
  }

  const onAttemptTouchMove = (e) => {
    if (!attemptTouchStart) return

    const currentX = e.targetTouches[0].clientX
    setAttemptTouchCurrent(currentX)

    const offset = currentX - attemptTouchStart

    // Only allow swipe right if there are previous attempts
    // Only allow swipe left if there are next attempts
    if (currentAttemptIndex > 0 && offset > 0) {
      // Swiping right, allow up to container width
      setAttemptSwipeOffset(Math.min(offset, window.innerWidth * 0.9))
    } else if (currentAttemptIndex < attempts.length - 1 && offset < 0) {
      // Swiping left, allow up to container width
      setAttemptSwipeOffset(Math.max(offset, -window.innerWidth * 0.9))
    }
  }

  const onAttemptTouchEnd = () => {
    if (!attemptTouchStart || !attemptTouchCurrent) {
      setAttemptSwipeOffset(0)
      return
    }

    const distance = attemptTouchStart - attemptTouchCurrent
    const isLeftSwipe = distance > minSwipeDistance
    const isRightSwipe = distance < -minSwipeDistance

    if (isLeftSwipe && currentAttemptIndex < attempts.length - 1) {
      setCurrentAttemptIndex(currentAttemptIndex + 1)
    }

    if (isRightSwipe && currentAttemptIndex > 0) {
      setCurrentAttemptIndex(currentAttemptIndex - 1)
    }

    // Reset touch positions and offset
    setAttemptTouchStart(null)
    setAttemptTouchCurrent(null)
    setAttemptSwipeOffset(0)
  }



  return (
    <div className="modal-overlay" onClick={(e) => {
      if (e.target.className === 'modal-overlay') {
        onClose()
      }
    }}>
      <div className="question-detail-modal">
        <div className="question-detail-header">
          <h2>Question Details</h2>
          <button className="close-button" onClick={onClose}>
            <Close />
          </button>
        </div>

        <div className="question-detail-content">
          <div className="question-detail-section">
            <h3>Question</h3>
            <div className="question-detail-text">
              <MixedContentPreview content={questionText} />
            </div>
          </div>

          {question.parentFeedback && question.parentFeedback.answerSummary && (
            <ParentAnswerSummary
              summary={question.parentFeedback.answerSummary}
              questionId={question.id}
              studentId={selectedStudent?.id} // Use selectedStudent prop
            />
          )}

          <div className="attempt-container">
            <div
              className="attempts-wrapper"
              onTouchStart={onAttemptTouchStart}
              onTouchMove={onAttemptTouchMove}
              onTouchEnd={onAttemptTouchEnd}
              style={{
                overflow: 'hidden',
                position: 'relative',
                width: '100%'
              }}
            >
              <div
                style={{
                  display: 'flex',
                  width: `${attempts.length * 100}%`,
                  transition: attemptTouchStart ? 'none' : 'transform 0.3s ease',
                  transform: attemptTouchStart
                    ? `translateX(calc(-${currentAttemptIndex * (100 / attempts.length)}% + ${attemptSwipeOffset}px))`
                    : `translateX(-${currentAttemptIndex * (100 / attempts.length)}%)`
                }}
              >
                {attempts.map((attempt, index) => {
                  const attemptAnswerText = attempt.answer?.latex ||
                    attempt.answer?.text ||
                    attempt.studentAnswer?.latex ||
                    attempt.studentAnswer?.text ||
                    (typeof attempt.answer === 'string' ? attempt.answer : 'No answer provided')

                  return (
                    <div
                      key={index}
                      className="attempt-panel"
                      style={{
                        flex: `0 0 ${100 / attempts.length}%`,
                        width: `${100 / attempts.length}%`,
                        padding: '0 10px',
                        boxSizing: 'border-box'
                      }}
                    >
                      <div className="attempt-header">
                        <div className="attempt-counter">
                          Attempt {currentAttemptIndex + 1} of {attempts.length}
                        </div>
                      </div>

                      <div className="question-detail-section">
                        <h3>Student's Answer</h3>
                        <div className="answer-detail-text">
                          <MixedContentPreview content={attemptAnswerText} />
                        </div>
                      </div>

                      {attempt.feedback && (
                        <div className="question-detail-section">
                          <h3>Feedback</h3>
                          <div className={`feedback-detail ${attempt.feedback.isCorrect ? 'correct' : 'incorrect'}`}>
                            <div className="feedback-status">
                              {attempt.feedback.isCorrect ? '✓ Correct' : '✗ Not Quite'}
                            </div>
                            <div className="feedback-message">
                              {attempt.feedback.message}
                            </div>
                          </div>
                        </div>
                      )}

                      <div className="attempt-navigation-hints">
                        {currentAttemptIndex > 0 && (
                          <div className="swipe-hint swipe-hint-attempt">
                            « Previous
                          </div>
                        )}
                        {currentAttemptIndex < attempts.length - 1 && (
                          <div className="swipe-hint swipe-hint-attempt">
                            Next »
                          </div>
                        )}
                      </div>

                    </div>
                  )
                })}
              </div>
            </div>
          </div>

          <div className="question-detail-date">
            Submitted: {question.timestamp?.toDate?.().toLocaleString() || 'Unknown date'}
          </div>

          {loadedVideoRecommendations.length > 0 && (
            <div className="question-detail-section">
              <h3>Recommended Videos</h3>
              <div className="video-recommendations-grid">
                {loadedVideoRecommendations.map((video, index) => (
                  <div
                    key={video.id || index}
                    className="video-recommendation-card"
                    onClick={() => window.open(video.url, '_blank')}
                  >
                    <div className="video-thumbnail">
                      {video.thumbnail ? (
                        <img src={video.thumbnail} alt={video.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      ) : (
                        <div className="video-play-icon">▶</div>
                      )}
                      <div className="video-platform">
                        {video.url?.includes('youtube.com') || video.url?.includes('youtu.be') ? 'YouTube' :
                         video.url?.includes('khanacademy.org') ? 'Khan Academy' : 'Video'}
                      </div>
                    </div>
                    <div className="video-title">{video.title}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default QuestionDetailModal